import { ChangeDetectionStrategy, Component, computed, input, output, inject, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

export type ButtonVariant = 'default' | 'secondary' | 'ternary' | 'text' | 'link';

@Component({
  selector: 'om-button',
  imports: [CommonModule, RouterLink],
  templateUrl: './button.html',
  styleUrl: './button.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Button {
  el = inject(ElementRef);
  label = input<string>('');
  iconLeft = input<string>('');
  iconRight = input<string>('');
  imageLeft = input<string>('');
  imageRight = input<string>('');
  variant = input<ButtonVariant>('default');
  /** When set, renders as an anchor with routerLink instead of a button (e.g. for link variant). */
  routerLink = input<string | null>(null);
  disabled = input<boolean>(false);
  isLoading = input<boolean>(false);
  type = input<'button' | 'submit'>('button');
  fullWidth = input<boolean>(false);
  size = input<'sm' | 'md' | 'lg'>('md');

  clicked = output<MouseEvent>();

  onClick(event: MouseEvent) {
    if (!this.disabled() && !this.isLoading()) {
      this.clicked.emit(event);
    }
  }

  /** Icon size kept at or below text line-height for visual balance. */
  iconSizeClass = computed(() => {
    if (this.variant() === 'link') return '!text-[16px]';
    switch (this.size()) {
      case 'sm':
        return '!text-[18px]';
      case 'md':
        return '!text-[20px]';
      case 'lg':
        return '!text-[22px]';
      default:
        return '!text-[20px]';
    }
  });

  // Styles aligned with stitch_login: rounded-xl, py-3.5 px-4 (md), active:scale-[0.99]
  buttonClasses = computed(() => {
    if (this.variant() === 'link') {
      return 'group w-fit flex active:scale-[0.99] cursor-pointer items-center gap-2 text-sm text-slate-500 dark:text-slate-300 transition-colors hover:text-primary dark:hover:text-primary font-medium rounded';
    }

    const baseClasses =
      'flex justify-center items-center rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all transform';
    const widthClass = this.fullWidth() ? 'w-full' : 'w-auto';
    const stateClasses =
      this.disabled() || this.isLoading()
        ? 'opacity-60 cursor-not-allowed'
        : 'active:scale-[0.99] cursor-pointer';

    let sizeClasses = '';
    switch (this.size()) {
      case 'sm':
        sizeClasses = 'h-10 text-sm gap-2 px-4';
        break;
      case 'md':
        sizeClasses = 'h-[52px] text-base gap-3 px-5';
        break;
      case 'lg':
        sizeClasses = 'h-14 text-lg gap-3 px-6';
        break;
    }

    let variantClasses = '';
    switch (this.variant()) {
      case 'default':
        variantClasses =
          'border border-transparent font-semibold text-white bg-primary hover:bg-primary-dark focus:ring-primary duration-300 backdrop-blur-sm shadow-[0_4px_20px_rgba(224,122,95,0.35)] dark:shadow-[0_4px_20px_rgba(224,122,95,0.25)]';
        break;
      case 'secondary':
        variantClasses =
          'bg-slate-800/85 dark:bg-white/[0.10] border border-white/[0.08] font-medium text-white hover:bg-slate-700 duration-200 focus:ring-slate-500 backdrop-blur-sm';
        break;
      case 'ternary':
        variantClasses =
          'bg-white/70 dark:bg-white/[0.08] border border-white/60 dark:border-white/[0.15] text-slate-700 dark:text-slate-200 hover:bg-white/85 dark:hover:bg-white/[0.14] font-medium duration-200 focus:ring-primary/50 transition-colors backdrop-blur-sm shadow-sm';
        break;
      case 'text':
        variantClasses =
          'text-primary border-transparent hover:bg-primary/10 font-medium duration-200 focus:ring-primary/50 shadow-none';
        break;
      case 'link':
        variantClasses = '';
        break;
    }

    return `${baseClasses} ${widthClass} ${sizeClasses} ${variantClasses} ${stateClasses}`;
  });
}
